import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import KnowledgeBaseService from '../../../services/KnowledgeBaseService';

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

const Row = ({ label, children }) => (
  <Typography variant="body2" sx={{ mb: 0.75 }}>
    <strong>{label}:</strong> {children}
  </Typography>
);

/**
 * Detalle de una base de conocimiento administrada por whisperbi. Todo lo que
 * muestra viene ya sanitizado del backend -- nunca ARNs, buckets ni el id de
 * Bedrock.
 */
const KnowledgeBaseAdminDialog = ({ open, knowledgeBaseId, onClose }) => {
  const [kb, setKb] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !knowledgeBaseId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setKb(null);
    KnowledgeBaseService.getKnowledgeBase(knowledgeBaseId)
      .then((data) => {
        if (!cancelled) setKb(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'No se pudo obtener el detalle.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, knowledgeBaseId]);

  const ingestion = kb?.last_ingestion;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <StorageIcon color="primary" />
          <Typography variant="h6">Base de conocimiento</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && kb && (
          <Box>
            <Row label="Nombre">{kb.name}</Row>
            <Row label="Modelo semántico">{kb.semantic_model_name || kb.semantic_model_id}</Row>
            <Row label="Estado">
              <Chip
                size="small"
                label={kb.status}
                color={kb.status === 'ACTIVE' ? 'success' : kb.status === 'FAILED' ? 'error' : 'default'}
              />
            </Row>
            {kb.status === 'FAILED' && kb.status_detail && (
              <Alert severity="error" sx={{ my: 1 }}>{kb.status_detail}</Alert>
            )}
            {kb.description && <Row label="Descripción">{kb.description}</Row>}
            <Row label="Modelo de embeddings">{kb.embedding_model}</Row>
            <Row label="Documentos">{kb.document_count ?? '—'}</Row>
            <Row label="Fuentes de datos">{kb.data_source_count ?? '—'}</Row>
            <Row label="Creada">{formatDate(kb.created_at)}</Row>
            <Row label="Actualizada">{formatDate(kb.updated_at)}</Row>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Última sincronización</Typography>
            {ingestion ? (
              <Box>
                <Row label="Estado">{ingestion.status}</Row>
                <Row label="Iniciada">{formatDate(ingestion.started_at)}</Row>
                <Row label="Finalizada">{formatDate(ingestion.finished_at)}</Row>
                <Row label="Documentos indexados">{ingestion.docs_indexed ?? '—'}</Row>
                <Row label="Documentos con error">{ingestion.docs_failed ?? '—'}</Row>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">Sin sincronizaciones registradas.</Typography>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Agentes vinculados ({(kb.linked_agents || []).length})
            </Typography>
            {(kb.linked_agents || []).length > 0 ? (
              <List dense>
                {kb.linked_agents.map((a) => (
                  <ListItem key={a.config_id} disableGutters>
                    <ListItemText primary={a.agent_name || a.config_id} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">Ningún agente usa esta base de conocimiento.</Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

KnowledgeBaseAdminDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  knowledgeBaseId: PropTypes.string,
  onClose: PropTypes.func.isRequired
};

KnowledgeBaseAdminDialog.defaultProps = {
  knowledgeBaseId: null
};

export default KnowledgeBaseAdminDialog;
